import axios from "axios";
import moment from "moment";
import { get, writable } from "svelte/store";

import LoadState from "../Enums/LoadState";
import GeoHelper from "../Helpers/GeoHelper";
import MapDataSource from "../Map/Enums/MapDataSource";
import MapLayer from "../Map/Enums/MapLayer";
import MapStore from "../Map/Store";
// import TimeSliderData from "../UI/TimeSlider/TimeSliderData";
import PopupStore from "../Map/Popup/PopupStore";

import { formatTime } from "../Helpers/Misc/timeHelper";
import Aircraft from "../Models/Aircraft";
import AircraftProperty from "../Models/Enums/AircraftProperty";
import VesselGroup from "../Models/VesselGroup";
import VesselGroupHelper from "../Models/VesselGroupHelper";

import Filters from "../Filters/Filters";
import AircraftFilterProperties from "./AircraftFilterProperties";

import API from "../API/API";

import arrayRekey from "../Helpers/Misc/arrayRekey";
import MapLayerManager from "../Map/MapLayerManager";
import { Layout, Paint } from "../Map/MapLayerStyles";

const AircraftStore = {
	config: {
		layers: [
			"_airports_gb.json",
			"datasets/mod-uk-aviation"
		]
	},
	meta: {
		loadState: writable(LoadState.INIT)
	},
	results: {
		group: new VesselGroup(),
		filtered: writable([]),

		airports: writable([]),
		airportsByIATA: writable({})
	},
	filters: {
		advanced: new Filters(AircraftFilterProperties),
		lastUpdated: writable(new Date(0))
	},
	actions: {
		deinit: () => {
			if (get(AircraftStore.meta.loadState) !== LoadState.LOADED) {
				return;
			}
			AircraftStore.results.airports.set([]);
			AircraftStore.results.airportsByIATA.set({});
			AircraftStore.results.group.vessels.set([]);
			AircraftStore.actions._deinitMap();
			AircraftStore.meta.loadState.set(LoadState.INIT);
		},
		init: () => {
			if (get(AircraftStore.meta.loadState) !== LoadState.INIT) {
				return;
			}
			AircraftStore.meta.loadState.set(LoadState.LOADING);

			AircraftStore.actions._initMap();

			const fileUrlAirports = window.location.origin + "/files/" + AircraftStore.config.layers[0];
			axios.get(fileUrlAirports)
				.then(airportresponse => {
					if (airportresponse.status !== 200) {
						throw new Error("Invalid Airport data.");
					}

					// Set airports
					AircraftStore.results.airports.set(airportresponse.data);
					AircraftStore.results.airportsByIATA.set(arrayRekey(airportresponse.data, "iata_code"));

					API.get(AircraftStore.config.layers[1])
						.then(response => {
							return { success: true, data: JSON.parse(response.dataset.content) };
						})
						.then((response) => {
							if (!response.success) {
								throw new Error("Invalid Aircraft data.");
							}
							// console.log("response", response);
							let maxTimestamp = 0;
							const aircraftData = [];
							const aircraftByRegistration = {};
							for (let i = 0; i < response.data.features.length; i++) {
								const f = response.data.features[i];

								if (f.attributes.registration == null) {
									continue;
								}

								const newf = GeoHelper.featureAt(f.geometry.x, f.geometry.y, f.attributes);
								if (!aircraftByRegistration[f.attributes.registration]) {
									aircraftByRegistration[f.attributes.registration] = [];
								}

								const parts = newf.properties.time.split(":");
								const addTime = moment({ year: 1970, month: 0, day: 1, hour: parseInt(parts[0]), minute: parseInt(parts[1]), second: parseInt(parts[2]) });
								newf.properties.timestamp = moment(new Date(newf.properties.date));
								newf.properties.timestamp.add(addTime);

								if (newf.properties.timestamp.unix() > maxTimestamp) {
									maxTimestamp = newf.properties.timestamp.unix();
								}

								aircraftByRegistration[f.attributes.registration].push(newf);
								aircraftData.push(newf);
							}

							// Fudge timestamps
							const diffSeconds = Math.round(moment().diff(moment.unix(maxTimestamp)) / 1000);
							for (let i = 0; i < aircraftData.length; i++) {
								aircraftData[i].properties.timestamp.add(diffSeconds, "seconds");
								aircraftData[i].properties.timestamp = aircraftData[i].properties.timestamp.format();
							}

							AircraftStore.results.group.uniqidType = AircraftProperty.REGISTRATION;

							const aircraftReg = Object.keys(aircraftByRegistration);
							const all = [];
							for (let i = 0; i < aircraftReg.length; i++) {
								const points = aircraftByRegistration[aircraftReg[i]];
								const aircraft = new Aircraft(points[points.length - 1], AircraftStore.results.group);
								aircraft.generateHistoricPath(points);
								all.push(aircraft);
							}

							AircraftStore.results.group.vessels.set(all);
							AircraftStore.meta.loadState.set(LoadState.LOADED);
							AircraftStore.actions.update();

							MapLayerManager.updateVisibleLayers();
						})
						.catch(error => {
							console.error("AircraftStore.init", error);
						});
				})
				.catch(error => {
					console.error("AircraftStore.init", error);
				});
		},
		_deinitMap: () => {
			const map = MapStore.helpers.getMap();

			map.removeLayer(MapLayer.AIRCRAFT_FAKE_HISTORIC_LINES);
			map.removeLayer(MapLayer.AIRCRAFT_FAKE_HISTORIC_POINTS);
			map.removeLayer(MapLayer.AIRCRAFT_FAKE_LIVE);

			map.removeSource(MapDataSource.AIRCRAFT_FAKE_HISTORIC_LINES);
			map.removeSource(MapDataSource.AIRCRAFT_FAKE_HISTORIC_POINTS);
			map.removeSource(MapDataSource.AIRCRAFT_FAKE_LIVE);
		},
		_initMap: () => {
			const map = MapStore.helpers.getMap();

			map.addSource(MapDataSource.AIRCRAFT_FAKE_HISTORIC_LINES, {
				type: "geojson",
				data: GeoHelper.emptyGeoJson()
			});
			map.addSource(MapDataSource.AIRCRAFT_FAKE_HISTORIC_POINTS, {
				type: "geojson",
				data: GeoHelper.emptyGeoJson()
			});
			map.addSource(MapDataSource.AIRCRAFT_FAKE_LIVE, {
				type: "geojson",
				data: GeoHelper.emptyGeoJson()
			});

			map.addLayer({
				id: MapLayer.AIRCRAFT_FAKE_LIVE,
				source: MapDataSource.AIRCRAFT_FAKE_LIVE,
				type: "symbol",
				layout: Layout.aircraft
			});
			map.addLayer(
				{
					id: MapLayer.AIRCRAFT_FAKE_HISTORIC_LINES,
					type: "line",
					source: MapDataSource.AIRCRAFT_FAKE_HISTORIC_LINES,
					paint: Paint.trails
				},
				"hillshade"
			);
			map.addLayer(
				{
					id: MapLayer.AIRCRAFT_FAKE_HISTORIC_POINTS,
					type: "symbol",
					source: MapDataSource.AIRCRAFT_FAKE_HISTORIC_POINTS,
					layout: Layout.historic
				},
				MapLayer.AIRCRAFT_FAKE_LIVE
			);
			map.setLayerZoomRange(MapLayer.AIRCRAFT_FAKE_HISTORIC_LINES, 0, 24);
			map.setLayerZoomRange(MapLayer.AIRCRAFT_FAKE_HISTORIC_POINTS, 0, 24);

			const enter = (e) => {
				const props = e.features[0].properties;
				const coords = e.features[0].geometry.coordinates;

				let html = "";
				html += `<strong>${props[AircraftProperty.REGISTRATION]}</strong><br/>`;
				html += `<i>${props[AircraftProperty.FLIGHT_NUMBER]}</i> operated by ${props[AircraftProperty.FLIGHT_OPERATOR]}<br/><br/>`;
				html += "<table>";

				html += `<tr>
							<td><b>Event:</b></td>
							<td>${props[AircraftProperty.STATE].toUpperCase()}</td>
						 </tr>`;

				html += `<tr>
							<td><b>Time:</b></td>
							<td>${formatTime(props[AircraftProperty.TIMESTAMP])}</td>
						 </tr>`;

				html += `<tr>
							<td><b>Aircraft:</b></td>
							<td>${props[AircraftProperty.EQUIPMENT]}</td>
						 </tr>`;

				html += `<tr>
							<td><b>Altitude:</b></td>
							<td>${props[AircraftProperty.ALTITUDE]} ft</td>
						 </tr>`;

				html += `<tr>
							<td><b>Call Sign:</b></td>
							<td>${props[AircraftProperty.CALL_SIGN]}</td>
						 </tr>`;

				html += `<tr>
							<td><b>Destination:</b></td>
							<td>${props[AircraftProperty.DESTINATION_IATA]} (${props[AircraftProperty.DESTINATION_ICAO]})</td>
						 </tr>`;

				html += `<tr>
							<td><b>Origin:</b></td>
							<td>${props[AircraftProperty.ORIGIN_IATA]} (${props[AircraftProperty.ORIGIN_ICAO]})</td>
						 </tr>`;

				html += "</table>";

				PopupStore.actions.customHtml(html, 400, coords, true);
			};
			const move = enter;
			// eslint-disable-next-line no-unused-vars
			const leave = (e) => {
				PopupStore.actions.custom({}, null, false);
			};

			map.on("mouseenter", MapLayer.AIRCRAFT_FAKE_LIVE, (e) => {
				enter(e);
			});
			map.on("mousemove", MapLayer.AIRCRAFT_FAKE_LIVE, (e) => {
				move(e);
			});
			map.on("mouseleave", MapLayer.AIRCRAFT_FAKE_LIVE, (e) => {
				leave(e);
			});

			map.on("mouseenter", MapLayer.AIRCRAFT_FAKE_HISTORIC_POINTS, (e) => {
				enter(e);
			});
			map.on("mousemove", MapLayer.AIRCRAFT_FAKE_HISTORIC_POINTS, (e) => {
				move(e);
			});
			map.on("mouseleave", MapLayer.AIRCRAFT_FAKE_HISTORIC_POINTS, (e) => {
				leave(e);
			});
		},
		updateFiltered: () => {
			const aircraft = get(AircraftStore.results.group.vessels);

			let filtered = aircraft;
			if (AircraftStore.filters.advanced.length > 0) {
				filtered = AircraftStore.filters.advanced.apply(aircraft);
			}
			// console.log("raw len", features.length);
			// console.log("fil len", filtered.length);
			AircraftStore.results.filtered.set(filtered);
		},
		update: () => {
			if (get(AircraftStore.meta.loadState) !== LoadState.LOADED) {
				return;
			}
			AircraftStore.actions.updateFiltered();

			const map = MapStore.helpers.getMap();
			const filtered = get(AircraftStore.results.filtered);
			const display = [];
			filtered.forEach((aircraft) => {
				// display.push(aircraft.currentFeature);
				if (aircraft.snapshotFeature) display.push(aircraft.snapshotFeature);
			});
			// console.log("display", display);
			// console.log("display", display);
			const fc = GeoHelper.emptyGeoJson(display);
			map.getSource(MapDataSource.AIRCRAFT_FAKE_LIVE).setData(fc);

			const pathGeoJson = VesselGroupHelper.toPathGeometry(filtered, (aircraft) => {
				return aircraft.selected && aircraft.snapshotFeature;
			});
			map.getSource(MapDataSource.AIRCRAFT_FAKE_HISTORIC_LINES).setData(pathGeoJson.lines);
			map.getSource(MapDataSource.AIRCRAFT_FAKE_HISTORIC_POINTS).setData(pathGeoJson.points);
		}
	}
};
export default AircraftStore;
